// src/hooks/useTestModels.ts

import { useDispatch, useSelector } from 'react-redux'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'
import { AppDispatch, RootState } from 'src/redux'
import {
  getConfigModelCreditLimit,
  getFiltersSettings,
  getInitialSettiongByVersion,
  testGetModelsCreditLimit,
} from 'src/services/Onboarding/TestModels/testModels.service'

/**
 * Custom hook for managing test models and their related actions.
 * It provides access to various response states and functions to fetch
 * information related to credit limits and model settings.
 *
 * @returns An object containing the responses and methods for handling
 *          test model operations.
 */
const useTestModels = () => {
  const {
    responseGetModelsCreditLimit,
    responseGetInitialSettiongByVersion,
    rsponseGetConfigModelCreditLimit,
    responseGetFilterSettings,
  } = useSelector((state: RootState) => state.testModel)

  const dispatch = useDispatch<AppDispatch>()
  const { startLoading, stopLoading } = useLoading()
  const { showAlert } = useAlert()

  /**
   * Tests the model credit limit by dispatching an action to get the
   * model's credit limit and shows alerts based on the result.
   */
  const testModelCreditLimit = () => {
    try {
      startLoading()
      dispatch(testGetModelsCreditLimit())
        .then((result: any) => {
          if (testGetModelsCreditLimit.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) ||
              'Error al obtener la información del usuario',
            )
          } else if (testGetModelsCreditLimit.fulfilled.match(result)) {
            showAlert(
              'success',
              'Modelo de límite de crédito obtenido correctamente',
            )
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener la información del usuario')
    }
  }

  /**
   * Fetches the filter settings for models and shows alerts based on the result.
   */
  const getFiltersModels = () => {
    try {
      startLoading()
      dispatch(getFiltersSettings())
        .then((result: any) => {
          if (getFiltersSettings.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) ||
              'Error al obtener los filtros de los modelos',
            )
          } else if (getFiltersSettings.fulfilled.match(result)) {
            showAlert(
              'success',
              'Filtros de los modelos obtenidos correctamente',
            )
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener los filtros de los modelos')
    }
  }

  /**
   * Tests the configuration of the model credit limit with the provided payload.
   *
   * @param payload - An object containing the model version ID.
   */
  const testConfigModelCreditLimit = (payload: { modelVersionId: number }) => {
    try {
      startLoading()
      dispatch(getConfigModelCreditLimit(payload))
        .then((result: any) => {
          if (getConfigModelCreditLimit.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) ||
              'Error al obtener la información del usuario',
            )
          } else if (getConfigModelCreditLimit.fulfilled.match(result)) {
            showAlert(
              'success',
              'Información del usuario obtenida correctamente',
            )
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener la información del usuario')
    }
  }

  /**
   * Tests the initial settings by version with the provided payload.
   *
   * @param payload - An object containing the setting version ID.
   */
  const testInitialSettiongByVersion = (payload: {
    settingVersionId: number
  }) => {
    try {
      startLoading()
      dispatch(getInitialSettiongByVersion(payload))
        .then((result: any) => {
          if (getInitialSettiongByVersion.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) ||
              'Error al obtener la configuración inicial',
            )
          } else if (getInitialSettiongByVersion.fulfilled.match(result)) {
            showAlert(
              'success',
              'Configuración inicial obtenida correctamente',
            )
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error) {
      showAlert('error', 'Error al obtener la configuración inicial')
    }
  }

  return {
    responseGetModelsCreditLimit,
    responseGetInitialSettiongByVersion,
    rsponseGetConfigModelCreditLimit,
    responseGetFilterSettings,
    getFiltersModels,
    testModelCreditLimit,
    testConfigModelCreditLimit,
    testInitialSettiongByVersion,
  }
}

export default useTestModels
